// artifact 이벤트 → HTML 문자열 — 바닐라 app.js renderArtifact 이식.
// 3-A: note/table/download/chart, 3-B 추가: kv/checklist/screen_panel
// (company_card 는 ChatPage 가 targets 상태로 반영 — 시각 요소 없음).
//
// HTML 문자열로 만드는 이유: 바닐라와 동일한 마크업이 그대로 스냅샷(messagesHtml)에
// 실려 바닐라 openSession(innerHTML 복원)·문서함 인덱싱과 호환되고, 클릭은 바닐라와
// 같은 위임(delegation) 방식으로 처리한다 (MessageList 컨테이너 onClick).
import { libKindOf, libPreviewable, normalizeDocUrl } from "../../lib/library";
import { chartHtml, escapeHtml, isChartSpec, mdLiteInline } from "../../lib/markdown";

export interface ChatArtifact {
  type?: string;
  title?: string;
  text?: string;
  rows?: unknown[][];
  expand?: boolean;
  rowSend?: string[];
  url?: string;
  label?: string;
  doc?: unknown;
  quiet?: boolean;
  spec?: { followups?: string[]; [k: string]: unknown };
  chart?: unknown;
  name?: string;
  ticker?: string;
  [k: string]: unknown;
}

const DL_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>';

/** 렌더 결과 — html 은 div.art 의 innerHTML, quiet 는 표시 없이 스냅샷에만 남김.
 *  attrs 는 컨테이너 div 에 붙는 추가 속성(screen_panel 의 data-spec — 스냅샷 영속). */
export interface ArtifactHtml {
  html: string;
  className: string;
  quiet?: boolean;
  attrs?: Record<string, string>;
}

export function renderArtifactHtml(a: ChatArtifact): ArtifactHtml | null {
  if (a.type === "table") {
    const rows = (a.rows || []) as unknown[][];
    const title = escapeHtml(a.title || "표");
    const n = rows.length;
    const PAGE = 10; // 한 번에 보여줄 행 수 — 넘치면 '더 보기'
    const expand = !!a.expand; // 스크리너처럼 표 자체가 답인 결과는 기본 펼침
    // 각 행을 클릭 가능한 리스트 항목으로 렌더 — 첫 컬럼이 회사명, 두 번째가 심볼
    const items = rows
      .map((r, i) => {
        const name = escapeHtml(r[0] || "");
        const sym = escapeHtml(r[1] || "");
        // http(s) 로 시작하거나 /agent/doc-proxy 상대경로인 값은 원문 링크로 렌더
        const rest = r
          .slice(2)
          .filter(Boolean)
          .map((v) => {
            const s = String(v);
            return /^https?:\/\//i.test(s) || s.startsWith("/agent/doc-proxy")
              ? `<a href="${escapeHtml(s)}" target="_blank" rel="noopener" class="art-row-link" onclick="event.stopPropagation()">원문 보기 ↗</a>`
              : escapeHtml(s);
          })
          .join(" · ");
        const label = sym ? `${name}<span class="art-row-sym">${sym}</span>` : name;
        const sub = rest ? `<span class="art-row-sub">${rest}</span>` : "";
        // rowSend[i] 가 있으면 그 문구를 그대로 전송 — 없으면 기존 기본값
        const sendVal =
          a.rowSend && a.rowSend[i] ? a.rowSend[i] : sym ? `${name} (${r[1]})` : name;
        return `<div class="art-row"${i >= PAGE ? " hidden" : ""} data-send="${escapeHtml(sendVal)}">${label}${sub}</div>`;
      })
      .join("");
    const more =
      n > PAGE ? `<button class="art-more" type="button">더 보기 (${PAGE}/${n})</button>` : "";
    return {
      className: "art",
      html:
        `<button class="art-table-toggle" aria-expanded="${expand}">` +
        `<span class="art-table-toggle-title">${title}</span>` +
        `<span class="art-table-toggle-count">${expand ? "닫기 ▴" : `${n}개 결과 보기 ▾`}</span>` +
        `</button>` +
        `<div class="art-table-list"${expand ? "" : " hidden"}>${items}${more}</div>`,
    };
  }
  if (a.type === "kv") {
    const items = (a["items"] || []) as Array<{ label?: unknown; value?: unknown }>;
    const rows = items
      .map(
        (it) =>
          `<div class="art-kv-item"><span>${escapeHtml(it.label)}</span><span>${escapeHtml(it.value)}</span></div>`,
      )
      .join("");
    return {
      className: "art",
      html:
        `<div class="art-head">${escapeHtml(a.title || "결과")}</div>` +
        `<div class="art-body">${rows || "<div class='art-note'>표시할 항목이 없습니다.</div>"}</div>`,
    };
  }
  if (a.type === "checklist") {
    const lis = ((a["items"] || []) as unknown[])
      .map((it) => `<li>${escapeHtml(it)}</li>`)
      .join("");
    const note = a["note"]
      ? `<div class="art-note">${escapeHtml(a["note"])}</div>`
      : "";
    return {
      className: "art",
      html:
        `<div class="art-head">${escapeHtml(a.title || "필요 정보")}</div>` +
        `<div class="art-body art-checklist"><ul>${lis}</ul>${note}</div>`,
    };
  }
  if (a.type === "screen_panel") {
    // 우측 결과 패널 데이터 + 채팅 안 힌트 바 — "전체는 우측 패널에서"가 눈에 보여야
    // 발견된다. data-spec 은 스냅샷에 실려 영속(복원 시 아이콘·바 활성, 자동 오픈은 안 함).
    const total = Number(a["total"]) || ((a.rows || []) as unknown[]).length;
    return {
      className: "art screen-panel-data",
      attrs: { "data-spec": JSON.stringify(a) },
      html:
        `<button class="panel-open-btn" type="button" data-panel-open>` +
        `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3v18"/><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M21 9H3"/><path d="M21 15H3"/></svg>` +
        `전체 ${total.toLocaleString()}개 기업 — 우측 결과 패널에서 보기 (정렬·검색·엑셀)</button>`,
    };
  }
  if (a.type === "note") {
    return {
      className: "art",
      html:
        `<div class="art-head">${escapeHtml(a.title || "")}</div>` +
        `<div class="art-body"><p style="margin:0;line-height:1.7;font-size:14px;">${mdLiteInline(a.text || "")}</p></div>`,
    };
  }
  if (a.type === "download") {
    // encodeURI 는 이미 %XX 인 서명 URL 을 %25XX 로 겹쳐 인코딩해 다운로드가 403 이 났다 — 정규화만 한다
    const href = normalizeDocUrl(String(a.url || ""));
    // doc 메타(종류·기업·생성일)는 anchor 속성으로 심는다 — 스냅샷 HTML 에 실려
    // 영속화되고, 문서함(바닐라·3-B)이 인덱스를 만들 때 그대로 읽는다.
    const docAttr = a.doc ? ` data-doc="${escapeHtml(JSON.stringify(a.doc))}"` : "";
    // 미리보기 버튼 — PDF(report)·엑셀/CSV(excel)만. 스냅샷으로 저장/복원되는
    // 마크업이라 인라인 onclick 을 넣지 않는다: React(MessageList 위임)에서만
    // 동작하고, 바닐라 페이지에서 복원되면 무동작 버튼(무해)으로 남는다.
    const kind = libKindOf(
      String(a.label || ""),
      (a.doc as Record<string, unknown> | null) || null,
      String(a.url || ""),
    );
    const previewBtn = libPreviewable(kind)
      ? `<button type="button" class="art-preview-btn" data-preview-url="${href}"` +
        ` data-preview-name="${escapeHtml(a.label || "문서")}" data-preview-kind="${kind}">미리보기</button>`
      : "";
    return {
      className: "art",
      quiet: !!a.quiet, // 채팅엔 표시하지 않고 스냅샷에만 남긴다
      html: `<div class="art-body art-download"><a href="${href}" target="_blank" rel="noopener" download${docAttr}>${DL_ICON}${escapeHtml(a.label || "다운로드")}</a>${previewBtn}</div>`,
    };
  }
  if (a.type === "chart") {
    // 툴이 직접 내려준 차트 스펙 (재무 추이·주가 등) — hydrate 는 ChatPage effect 가 수행
    const spec = a.spec || a.chart;
    if (!isChartSpec(spec)) return null;
    return { className: "art art-chart", html: chartHtml(spec) };
  }
  return null; // company_card 등 시각 요소 없는 아티팩트
}
